import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * 对话框（含移动端底部抽屉形态）。
 *
 * 手写而不是引第三方库，原因有两个：
 * 1. 依赖已经不少，一个"打开/关闭 + Esc + 遮罩"的对话框不值得再加包；
 * 2. 但**几个无障碍细节必须做对**，否则键盘与读屏用户会被卡住：
 *    - Esc 关闭、点遮罩关闭；
 *    - 打开时把焦点移到面板里，关闭后**还给触发它的元素**（这也是移动端"点词卡片"
 *      能连续点下一个词的前提）；
 *    - 打开时锁 `body` 滚动，避免背景跟着滚。
 *
 * 注意：这里没有做完整的焦点陷阱（Tab 可以走到背景）。对本站的用途（读词卡、导入表单）
 * 足够，且避免了焦点陷阱实现里最容易出的"焦点丢失"问题。若将来放进登录等关键流程，
 * 需要补上焦点环。
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  /** 移动端从底部滑出（`sheet`），桌面端居中（`center`） */
  variant = 'center',
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'center' | 'sheet';
  className?: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // 焦点进面板：没有可聚焦元素时聚焦面板本身
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-black/40 backdrop-blur-[1px]',
        variant === 'sheet' ? 'items-end sm:items-center sm:justify-center' : 'items-center justify-center p-4',
      )}
      // 点遮罩关闭：用 onMouseDown 而不是 onClick，避免"按住拖到面板外"被误判为关闭
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'flex max-h-[90dvh] w-full flex-col border bg-card text-card-foreground shadow-lg outline-none',
          variant === 'sheet' ? 'rounded-t-2xl sm:max-w-lg sm:rounded-lg' : 'rounded-lg sm:max-w-lg',
          className,
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t px-4 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
